import React, { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import type { MarketingRouterRule } from '~/types/marketingRouterRule';

import {
  type AdUrlExtraction,
  applyExtractionToMatch,
  extractMatchFromAdUrl,
  hasExtractableMatchValues,
  replaceExtractionInMatch,
} from './marketingRouterAdUrl';
import { MATCH_FIELD_LABELS } from './marketingRouterMatch';

type Match = MarketingRouterRule['match'];

interface Props {
  match: Match;
  onChange: (match: Match) => void;
  /** The brand's region codes, so an unknown one is not offered as a region. */
  regionCodes: Array<string>;
}

const FIELD_LABELS = new Map(MATCH_FIELD_LABELS);

/**
 * Drops a region the brand does not sell in, reporting it so the caller can
 * warn instead of claiming every field was filled.
 *
 * The Regions field only offers the brand's own codes, and svc-promo rejects
 * anything else on save — but the portal strips the segment before matching
 * either way, so dropping it silently leaves the rule matching every region.
 */
function withKnownRegionOnly(extraction: AdUrlExtraction, regionCodes: Array<string>): AdUrlExtraction {
  const { regions, ...values } = extraction.values;

  if (regions == null || regionCodes.includes(regions)) {
    return extraction;
  }

  return { ...extraction, values, unknownRegion: regions };
}

export default function MarketingRouterAdUrlField({ match, onChange, regionCodes }: Props) {
  const [adUrl, setAdUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [extraction, setExtraction] = useState<AdUrlExtraction | null>(null);
  // Held while the operator decides whether to add to or replace the values
  // already in the match; see AC5.
  const [pendingExtraction, setPendingExtraction] = useState<AdUrlExtraction | null>(null);
  // The match this field last produced, so an edit made anywhere else is told
  // apart from its own change.
  const appliedMatch = useRef<Match | null>(null);

  // The messages describe the match the URL filled. Editing a Match field
  // afterwards leaves them describing something that is no longer true, so
  // they are cleared as soon as the match changes by any other hand.
  useEffect(() => {
    if (appliedMatch.current != null && appliedMatch.current !== match) {
      appliedMatch.current = null;
      setExtraction(null);
    }
  }, [match]);

  const resolvePending = (mode: 'add' | 'replace') => () => {
    if (pendingExtraction != null) {
      applyExtraction(pendingExtraction, mode);
    }
  };

  const applyExtraction = (result: AdUrlExtraction, mode: 'add' | 'replace') => {
    const applied =
      mode === 'replace'
        ? replaceExtractionInMatch(match, result.values)
        : applyExtractionToMatch(match, result.values);

    appliedMatch.current = applied;
    onChange(applied);
    setExtraction(result);
    setPendingExtraction(null);
    // The box empties once its values are in the fields below. The URL is not
    // part of the rule, so leaving it sitting there reads as a saved value;
    // and one rule commonly serves several campaigns, so the next paste is the
    // likely next action. The messages stay standing — they describe the
    // extraction, not the box.
    setAdUrl('');
  };

  const handleExtract = () => {
    const result = withKnownRegionOnly(extractMatchFromAdUrl(adUrl), regionCodes);

    if (result.error != null) {
      setError(result.error);
      setExtraction(null);
      return;
    }

    setError(null);

    // Asking every time would make the common case — an empty match on a new
    // rule — a two-step action for no gain.
    if (hasExtractableMatchValues(match, result.values)) {
      setPendingExtraction(result);
      return;
    }

    applyExtraction(result, 'add');
  };

  const unknownRegion = extraction?.unknownRegion;

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
        <TextField
          label="Advertising URL"
          fullWidth
          value={adUrl}
          onChange={(event) => {
            setAdUrl(event.target.value);
            // The messages describe the URL that was read, not the one being
            // typed. Clearing them only resets the messages: the values
            // already extracted stay in the fields below, as AC8 requires.
            setError(null);
            setExtraction(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              // The field sits inside the rule form, and Enter in a text input
              // would otherwise submit the rule rather than read the URL.
              event.preventDefault();
              handleExtract();
            }
          }}
          error={error != null}
          helperText={
            error ??
            'Optional. Paste a complete advertising URL to fill the fields below. Not saved on the rule — the fields below are what the rule matches on.'
          }
          placeholder="https://luxuryescapes.com/au/search?utm_source=google&utm_medium=cpc&utm_campaign=22931693244"
        />
        <Button variant="contained" color="primary" onClick={handleExtract} sx={{ flexShrink: 0, mt: { sm: 1 } }}>
          Extract UTM details
        </Button>
      </Stack>

      {unknownRegion != null ? (
        <Alert severity="warning">
          That URL starts with /{unknownRegion.toLowerCase()}, which is not a region this brand sells in, so{' '}
          {FIELD_LABELS.get('regions')} was left empty. As it stands the rule matches that page in every region.
        </Alert>
      ) : null}

      {extraction != null && unknownRegion == null ? (
        <Alert severity="success">Filled the fields below from that URL. Review and edit them before saving.</Alert>
      ) : null}

      <Dialog open={pendingExtraction != null} onClose={() => setPendingExtraction(null)}>
        <DialogTitle>Replace the values already set?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The Match fields already hold values. Add this URL’s values alongside them to widen the rule, or replace the
            values this URL carries — fields it says nothing about are left as they are.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={resolvePending('add')}>
            Add to existing
          </Button>
          <Button variant="text" color="primary" onClick={resolvePending('replace')}>
            Replace
          </Button>
          <Button variant="text" color="primary" onClick={() => setPendingExtraction(null)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
